import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import { apiBaseUrl, useApiQuery } from "../../../services/api";
import { getFirebaseToken } from "../../../services/firebase";
import Loader from "../../Loader";
import type { ChatAttachedFile } from "../../../domains/gpt/hooks/useChatAttachments";
import type { EnsuredConversation } from "../../../domains/gpt/hooks/useEnsureGptConversation";

// Preview of a file the user attached. The stored object is private, so the bytes
// come from an authorized route — which is also why the PDF path builds a blob URL
// rather than handing `PdfViewer` a GCS link: pdf.js fetches the URL itself and
// would carry no Bearer token.
//
// The two arms use DIFFERENT endpoints on purpose. A PDF is streamed whole from
// `/content` (the viewer needs the real document). A text file comes from
// `/text-preview`, which caps it server-side — rendering a 7 MB export as one DOM
// text node janks the page for seconds, and capping on the server means a big
// library still previews as its head instead of refusing.
const LazyPdfViewer = lazy(() => import("../../PdfViewer"));

/**
 * A Blob rendered by the PDF viewer. Owns the object-URL lifecycle — creating one
 * without revoking it leaks the whole file until the tab closes, and a
 * multi-megabyte PDF is not a leak worth shrugging at. UI machinery, hence the
 * effect.
 */
function PdfBlobViewer({ blob }: { blob: Blob }): React.ReactElement {
  const url = useMemo(() => URL.createObjectURL(blob), [blob]);
  useEffect(
    () => () => {
      URL.revokeObjectURL(url);
    },
    [url]
  );
  return (
    <Suspense fallback={<Loader expanded />}>
      <LazyPdfViewer
        url={url}
        loadingFallback={<Loader expanded />}
        controls={{ kind: "floating" }}
        display={{ kind: "continuous" }}
      />
    </Suspense>
  );
}

// The bytes come from a plain authenticated `fetch`, NOT `useApiQuery`: `/content`
// returns a raw `Response` so it is not published in the openapi spec as JSON,
// which means the typed client has no body type for it. A binary endpoint has no
// JSON contract to begin with — fetching it directly is the honest consequence of
// that, not a workaround.
type PdfFetchState =
  { status: "loading" } | { status: "error" } | { status: "ready"; blob: Blob };

function PdfPreview({
  conversation,
  file,
}: {
  conversation: EnsuredConversation;
  file: ChatAttachedFile;
}): React.ReactElement {
  const { t } = useTranslation();
  const [state, setState] = useState<PdfFetchState>({ status: "loading" });

  useEffect(() => {
    // A `cancelled` flag rather than an AbortController: switching files unmounts
    // this component, and the only thing to avoid is setting state after that.
    let cancelled = false;
    void (async () => {
      try {
        const token = await getFirebaseToken();
        const response = await fetch(
          conversation.engine === "assistant"
            ? `${apiBaseUrl}/assistant/conversations/${conversation.id}/documents/${file.documentId}/content`
            : `${apiBaseUrl}/webapp/private/conversations/${conversation.id}/documents/${file.documentId}/content`,
          {
            headers: token === null ? {} : { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        const blob = await response.blob();
        if (!cancelled) setState({ status: "ready", blob });
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation.id, conversation.engine, file.documentId]);

  if (state.status === "loading") return <Loader expanded />;
  if (state.status === "error")
    return (
      <p className="p-6 text-center font-dm-sans text-sm text-[#7a6d65]">
        {t("Couldn't open this file.")}
      </p>
    );
  return <PdfBlobViewer blob={state.blob} />;
}

function TextPreview({
  conversation,
  file,
}: {
  conversation: EnsuredConversation;
  file: ChatAttachedFile;
}): React.ReactElement {
  const { t } = useTranslation();
  const legacyQuery = useApiQuery(
    "/webapp/private/conversations/:id/documents/:documentId/text-preview",
    conversation.engine === "legacy"
      ? { param: { id: conversation.id, documentId: file.documentId } }
      : false
  );
  const assistantQuery = useApiQuery(
    "/assistant/conversations/:conversationId/documents/:documentId/text-preview",
    conversation.engine === "assistant"
      ? {
          param: {
            conversationId: conversation.id,
            documentId: file.documentId,
          },
        }
      : false
  );
  const query =
    conversation.engine === "assistant" ? assistantQuery : legacyQuery;

  if (query.state === "idle")
    throw new Error("should not happen: the engine's preview query is idle");
  if (query.state === "pending") return <Loader expanded />;
  if (query.state === "error")
    return (
      <p className="p-6 text-center font-dm-sans text-sm text-[#7a6d65]">
        {t("Couldn't open this file.")}
      </p>
    );

  return (
    <>
      {query.body.truncated && (
        <p className="m-0 border-b border-ds-slate-100 px-4 py-2 font-dm-sans text-xs text-[#7a6d65]">
          {t("Showing the beginning of {{count}} lines.", {
            count: query.body.lineCount,
          })}
        </p>
      )}
      {/* `break-all`: an export can be one enormous line with no spaces to break at. */}
      <pre className="m-0 p-4 font-mono text-xs break-all whitespace-pre-wrap text-ds-mint-900">
        {query.body.text}
      </pre>
    </>
  );
}

export function ChatAttachmentPreview({
  conversation,
  file,
  onClose,
}: {
  conversation: EnsuredConversation;
  file: ChatAttachedFile;
  onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      <div className="flex items-center gap-3 border-b border-ds-slate-100 px-4 py-3 pt-[calc(12px+var(--safe-area-top))]">
        <span className="min-w-0 flex-1 truncate font-dm-sans text-sm font-semibold text-ds-mint-900">
          {file.filename}
        </span>
        <button
          type="button"
          aria-label={t("Close")}
          className="flex shrink-0 cursor-pointer items-center border-none bg-transparent p-1 text-ds-mint-900"
          onClick={onClose}
        >
          <IconX className="size-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {file.kind === "paged" ? (
          <PdfPreview conversation={conversation} file={file} />
        ) : (
          <TextPreview conversation={conversation} file={file} />
        )}
      </div>
    </div>
  );
}
